/**
 * Cliente HTTP da API.
 *
 * Tokens ficam em `localStorage`. Já estiveram em `sessionStorage`, para sumir
 * ao fechar a aba num computador compartilhado, mas o preço era alto demais:
 * `sessionStorage` é **por aba**, então abrir qualquer link com o botão do meio
 * caía na tela de login, e fechar a aba obrigava a entrar de novo. Na prática
 * a proteção rendia menos que o incômodo — quem usa o sistema o dia inteiro
 * reentrava a toda hora, e senha digitada muitas vezes por dia é pior para a
 * segurança do que token guardado.
 *
 * O limite de exposição passa a ser o prazo do refresh token (sete dias) e o
 * botão Sair, que revoga no servidor.
 *
 * Quando o access token expira, o interceptor renova uma única vez e refaz a
 * requisição. Renovações concorrentes compartilham a mesma promise, senão dez
 * requisições paralelas disparariam dez rotações — e como o servidor derruba
 * todas as sessões ao ver um refresh token reusado, isso não é só desperdício:
 * é logout geral.
 */

import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { baseDaApi } from '../utils/dominio';

// Caminho relativo por padrão: em desenvolvimento o Vite faz proxy de /api para
// o backend, então front e API compartilham a origem. Aponte `VITE_API_URL`
// para a URL absoluta quando a API estiver em outro domínio.
const BASE_URL = baseDaApi();

const CHAVE_ACCESS = 'portal.access_token';
const CHAVE_REFRESH = 'portal.refresh_token';
const CHAVE_USUARIO = 'portal.usuario';

/** Navegador em modo restrito pode barrar o acesso ao storage; sem isto a
 *  aplicação inteira quebraria no primeiro `getItem`. */
function ler(chave: string): string | null {
  try {
    return localStorage.getItem(chave);
  } catch {
    return null;
  }
}

function gravar(chave: string, valor: string): void {
  try {
    localStorage.setItem(chave, valor);
  } catch {
    // Sem storage a sessão vale só enquanto a página estiver aberta.
  }
}

export const armazenamento = {
  get access(): string | null {
    return ler(CHAVE_ACCESS);
  },
  get refresh(): string | null {
    return ler(CHAVE_REFRESH);
  },
  get usuario(): string | null {
    return ler(CHAVE_USUARIO);
  },
  salvarTokens(access: string, refresh: string): void {
    gravar(CHAVE_ACCESS, access);
    gravar(CHAVE_REFRESH, refresh);
  },
  salvarUsuario(usuario: unknown): void {
    gravar(CHAVE_USUARIO, JSON.stringify(usuario));
  },
  limpar(): void {
    try {
      localStorage.removeItem(CHAVE_ACCESS);
      localStorage.removeItem(CHAVE_REFRESH);
      localStorage.removeItem(CHAVE_USUARIO);
    } catch {
      // Nada a limpar se o storage não está disponível.
    }
  },
  /** Avisa quando outra aba entrou ou saiu, para as duas andarem juntas. */
  aoMudarEmOutraAba(callback: () => void): () => void {
    const ouvinte = (evento: StorageEvent) => {
      if (evento.key === CHAVE_ACCESS || evento.key === null) callback();
    };
    window.addEventListener('storage', ouvinte);
    return () => window.removeEventListener('storage', ouvinte);
  },
};

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = armazenamento.access;
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Upload precisa que o navegador monte o `Content-Type` com o boundary do
  // multipart. O padrão `application/json` da instância sobrescreveria isso, e
  // o servidor receberia um corpo que não sabe ler — todos os campos chegam
  // como ausentes, sem erro de rede que denuncie a causa.
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

/** Chamado quando a sessão morre de vez — a aplicação reage voltando ao login. */
let aoPerderSessao: (() => void) | null = null;
export function registrarPerdaDeSessao(callback: () => void): void {
  aoPerderSessao = callback;
}

let renovacaoEmCurso: Promise<string> | null = null;

async function renovarToken(): Promise<string> {
  const refresh = armazenamento.refresh;
  if (!refresh) throw new Error('sem refresh token');

  try {
    // `axios` puro: usar a instância `api` reentraria no interceptor.
    const resposta = await axios.post(`${BASE_URL}/auth/refresh`, { refresh_token: refresh });
    armazenamento.salvarTokens(resposta.data.access_token, resposta.data.refresh_token);
    return resposta.data.access_token;
  } catch (erro) {
    // Com o token no `localStorage` as abas dividem a mesma credencial, e duas
    // renovando ao mesmo tempo fazem a segunda apresentar um token já
    // rotacionado. O servidor trata reúso como vazamento e encerra **todas** as
    // sessões — o usuário seria deslogado por ter duas abas abertas.
    //
    // Antes de desistir, conferir o storage: se o token mudou, outra aba já
    // renovou com sucesso e a sessão está viva. Só não era esta aba quem devia
    // ter renovado.
    const atual = armazenamento.refresh;
    const acesso = armazenamento.access;
    if (acesso && atual && atual !== refresh) return acesso;
    throw erro;
  }
}

/** Revoga a sessão no servidor com os tokens de quem saiu.
 *
 * Roda depois que a tela já voltou ao login, então não pode passar pela
 * instância `api`: os interceptores leem o storage, que a essa altura está
 * vazio — ou já tem a sessão de quem entrou em seguida, e um 401 aqui
 * renovaria ou derrubaria a sessão errada. Com o access vencido, renova uma
 * vez com o refresh guardado e tenta de novo.
 */
export async function revogarSessao(access: string | null, refresh: string | null): Promise<void> {
  const encerrar = (token: string) =>
    axios.post(`${BASE_URL}/auth/logout`, null, { headers: { Authorization: `Bearer ${token}` } });

  if (access) {
    try {
      await encerrar(access);
      return;
    } catch (erro) {
      if (!axios.isAxiosError(erro) || erro.response?.status !== 401) throw erro;
    }
  }
  if (!refresh) return;
  const resposta = await axios.post(`${BASE_URL}/auth/refresh`, { refresh_token: refresh });
  await encerrar(resposta.data.access_token);
}

api.interceptors.response.use(
  (resposta) => resposta,
  async (erro: AxiosError) => {
    const original = erro.config as InternalAxiosRequestConfig & { _repetida?: boolean };
    const ehLogin = original?.url?.includes('/auth/login');

    if (erro.response?.status !== 401 || original?._repetida || ehLogin) {
      return Promise.reject(erro);
    }

    original._repetida = true;
    try {
      renovacaoEmCurso ??= renovarToken().finally(() => {
        renovacaoEmCurso = null;
      });
      const novo = await renovacaoEmCurso;
      original.headers.Authorization = `Bearer ${novo}`;
      return api(original);
    } catch (falha) {
      // Só derruba a sessão quando o servidor a recusou. API fora do ar (deploy,
      // reinício, rede) responde 502/sem resposta — e isso deslogava todo mundo
      // que estivesse com a tela aberta durante a subida.
      if (sessaoRecusada(falha)) {
        armazenamento.limpar();
        aoPerderSessao?.();
      }
      return Promise.reject(erro);
    }
  },
);

/** O servidor disse que a sessão não vale mais — e não só "não deu para falar com ele". */
export function sessaoRecusada(falha: unknown): boolean {
  if (!axios.isAxiosError(falha)) return true; // sem refresh token guardado
  const status = falha.response?.status;
  return status === 400 || status === 401 || status === 403;
}

/** Transforma o erro de um campo em frase legível.
 *
 * Validação de corpo inteiro no Pydantic chega sem nome de campo e com o
 * prefixo `Value error,` grudado na frente — a tela mostrava
 * ": Value error, Registre a deliberação…". O texto depois do prefixo é a
 * frase que o schema escreveu para o usuário ler; é só ela que interessa.
 */
function descreverCampo({ campo, problema }: { campo: string; problema: string }): string {
  const limpo = problema.replace(/^\s*(Value|Assertion)\s+error,\s*/i, '').trim();
  return campo ? `${campo}: ${limpo}` : limpo;
}

/** Extrai a mensagem legível do envelope de erro padronizado da API. */
export function mensagemDeErro(erro: unknown, padrao = 'Não foi possível concluir a operação.'): string {
  if (axios.isAxiosError(erro)) {
    const corpo = erro.response?.data as
      | { erro?: { mensagem?: string; campos?: { campo: string; problema: string }[] } }
      | undefined;
    if (corpo?.erro?.campos?.length) {
      return corpo.erro.campos.map(descreverCampo).join(' · ');
    }
    if (corpo?.erro?.mensagem) return corpo.erro.mensagem;
    if (erro.code === 'ECONNABORTED') return 'A requisição demorou demais. Tente novamente.';
    if (!erro.response) return 'Não foi possível falar com o servidor. Ele está no ar?';
  }
  return padrao;
}
